import React, { useCallback, useEffect, useState } from 'react'
import {
  InputField,
  Modal,
  SelectionBoxGroup,
  type SelectionBoxOption,
} from '@enamelai/design-system'

export type ScheduleInterval = 'day' | 'week' | 'month' | 'year' | 'quarter'
export type MonthDayChoice = '15' | '30' | 'custom'
export type WeekdayChoice =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday'

export const WEEKDAY_OPTIONS: ReadonlyArray<{
  id: WeekdayChoice
  label: string
}> = [
  { id: 'monday', label: 'Monday' },
  { id: 'tuesday', label: 'Tuesday' },
  { id: 'wednesday', label: 'Wednesday' },
  { id: 'thursday', label: 'Thursday' },
  { id: 'friday', label: 'Friday' },
  { id: 'saturday', label: 'Saturday' },
  { id: 'sunday', label: 'Sunday' },
]

export const SCHEDULE_INTERVAL_OPTIONS: SelectionBoxOption[] = [
  { value: 'day', title: 'Daily' },
  { value: 'week', title: 'Weekly' },
  { value: 'month', title: 'Monthly' },
  { value: 'quarter', title: 'Quarterly' },
  { value: 'year', title: 'Annual' },
]

export const MONTH_DAY_OPTIONS: SelectionBoxOption[] = [
  { value: '15', title: '15th day' },
  { value: '30', title: '30th day' },
  { value: 'custom', title: 'Custom' },
]

type ScheduleReportModalProps = {
  show: boolean
  onHide: () => void
  /** Shown in title, e.g. "Claims Performance" */
  reportName: string
  /** Called with the human-readable schedule line for the success toast body. */
  onSaveSchedule: (scheduleSummaryLine: string, scheduleDetails: any) => void
}

export function ordinalSuffix(n: number): string {
  const j = n % 10
  const k = n % 100
  if (k >= 11 && k <= 13) return 'th'
  if (j === 1) return 'st'
  if (j === 2) return 'nd'
  if (j === 3) return 'rd'
  return 'th'
}

function weekdayLabel(day: WeekdayChoice): string {
  return WEEKDAY_OPTIONS.find((o) => o.id === day)?.label ?? day
}

export function buildScheduleSummaryLine(
  reportName: string,
  interval: ScheduleInterval,
  monthDayChoice: MonthDayChoice,
  customDay: number,
  weekday: WeekdayChoice,
): string {
  switch (interval) {
    case 'day':
      return `${reportName} has been scheduled to run every day.`
    case 'week':
      return `${reportName} has been scheduled to run every ${weekdayLabel(weekday)}.`
    case 'year':
      return `${reportName} has been scheduled to run every year.`
    case 'quarter':
      return `${reportName} has been scheduled to run every quarter.`
    case 'month': {
      const day =
        monthDayChoice === '15' ? 15 : monthDayChoice === '30' ? 30 : customDay
      const o = ordinalSuffix(day)
      return `${reportName} has been scheduled every ${day}${o} day of the month.`
    }
    default:
      return `${reportName} has been scheduled.`
  }
}

/** Best-effort parse of stored summary lines into schedule form defaults. */
export function parseScheduleFromSummaryLine(summaryLine: string): {
  interval: ScheduleInterval
  weekday: WeekdayChoice
  monthDayChoice: MonthDayChoice
  customDay: number
} {
  const lower = summaryLine.toLowerCase()
  if (lower.includes('every day')) {
    return {
      interval: 'day',
      weekday: 'friday',
      monthDayChoice: 'custom',
      customDay: 12,
    }
  }
  if (lower.includes('every quarter')) {
    return {
      interval: 'quarter',
      weekday: 'friday',
      monthDayChoice: 'custom',
      customDay: 12,
    }
  }
  if (lower.includes('every year')) {
    return {
      interval: 'year',
      weekday: 'friday',
      monthDayChoice: 'custom',
      customDay: 12,
    }
  }
  const weekdayMatch = WEEKDAY_OPTIONS.find((d) =>
    lower.includes(`every ${d.label.toLowerCase()}`),
  )
  if (weekdayMatch || lower.includes('every week')) {
    return {
      interval: 'week',
      weekday: weekdayMatch?.id ?? 'friday',
      monthDayChoice: 'custom',
      customDay: 12,
    }
  }
  const dayMatch = lower.match(/every (\d+)(?:st|nd|rd|th) day of the month/)
  if (dayMatch) {
    const day = parseInt(dayMatch[1]!, 10)
    if (day === 15) {
      return {
        interval: 'month',
        weekday: 'friday',
        monthDayChoice: '15',
        customDay: 12,
      }
    }
    if (day === 30) {
      return {
        interval: 'month',
        weekday: 'friday',
        monthDayChoice: '30',
        customDay: 12,
      }
    }
    return {
      interval: 'month',
      weekday: 'friday',
      monthDayChoice: 'custom',
      customDay: Number.isFinite(day) ? day : 12,
    }
  }
  return {
    interval: 'month',
    weekday: 'friday',
    monthDayChoice: 'custom',
    customDay: 12,
  }
}

function ScheduleReportModal({
  show,
  onHide,
  reportName,
  onSaveSchedule,
}: ScheduleReportModalProps): React.ReactElement {
  const [interval, setInterval] = useState<ScheduleInterval>('month')
  const [weekday, setWeekday] = useState<WeekdayChoice>('friday')
  const [monthDayChoice, setMonthDayChoice] = useState<MonthDayChoice>('custom')
  const [customDay, setCustomDay] = useState<number>(12)

  useEffect(() => {
    if (show) {
      setInterval('month')
      setWeekday('friday')
      setMonthDayChoice('custom')
      setCustomDay(12)
    }
  }, [show])

  const getSelectedScheduleDetails = useCallback(() => {
    const every = interval
    let every_details: string | undefined = undefined
    if (every === 'week') {
      every_details = weekday
    } else if (every === 'month') {
      every_details =
        monthDayChoice === 'custom'
          ? customDay.toString()
          : monthDayChoice.toString()
    }
    return {
      every,
      every_details,
    }
  }, [interval, weekday, monthDayChoice, customDay])

  const handleSave = useCallback(() => {
    const day =
      monthDayChoice === '15' ? 15 : monthDayChoice === '30' ? 30 : customDay
    if (interval === 'month' && (day < 1 || day > 31)) {
      return
    }
    const line = buildScheduleSummaryLine(
      reportName,
      interval,
      monthDayChoice,
      customDay,
      weekday,
    )
    onSaveSchedule(line, getSelectedScheduleDetails())
    onHide()
  }, [
    customDay,
    getSelectedScheduleDetails,
    interval,
    monthDayChoice,
    onHide,
    onSaveSchedule,
    reportName,
    weekday,
  ])

  const customDayInvalid =
    interval === 'month' &&
    monthDayChoice === 'custom' &&
    (customDay < 1 || customDay > 31 || Number.isNaN(customDay))

  return (
    <Modal
      open={show}
      onClose={onHide}
      title="Schedule report"
      size="sm"
      className="reports-schedule-report-modal"
      bodyClassName="reports-schedule-report-modal__body"
      secondaryLabel="Cancel"
      primaryLabel="Save schedule"
      primaryDisabled={customDayInvalid}
      onSecondaryClick={onHide}
      onPrimaryClick={handleSave}
    >
      <div className="reports-schedule-report-modal__card">
        <SelectionBoxGroup
          name="schedule-interval"
          label="Generate this report every:"
          value={interval}
          options={SCHEDULE_INTERVAL_OPTIONS}
          onChange={(value) => setInterval(value as ScheduleInterval)}
          className="reports-schedule-report-modal__selection-boxes"
        />
      </div>

      {interval === 'week' ? (
        <div className="reports-schedule-report-modal__card">
          <SelectionBoxGroup
            name="schedule-weekday"
            label="Select a day of the week"
            value={weekday}
            options={WEEKDAY_OPTIONS.map(({ id, label }) => ({
              value: id,
              title: label,
            }))}
            onChange={(value) => setWeekday(value as WeekdayChoice)}
            className="reports-schedule-report-modal__selection-boxes"
          />
        </div>
      ) : null}

      {interval === 'month' ? (
        <>
          <div className="reports-schedule-report-modal__card">
            <SelectionBoxGroup
              name="schedule-month-day"
              label="Select a day of the month"
              value={monthDayChoice}
              options={MONTH_DAY_OPTIONS}
              onChange={(value) => setMonthDayChoice(value as MonthDayChoice)}
              className="reports-schedule-report-modal__selection-boxes"
            />
          </div>

          {monthDayChoice === 'custom' ? (
            <div className="reports-schedule-report-modal__card">
              <span className="reports-schedule-report-modal__card-label">
                Set a custom day of the month
              </span>
              <div className="reports-schedule-report-modal__custom-day">
                <span className="reports-schedule-report-modal__custom-day-prefix">
                  Every:
                </span>
                <InputField
                  id="schedule-custom-day-input"
                  label=""
                  aria-label="Custom day of the month"
                  variant="number"
                  numberMin={1}
                  numberMax={31}
                  className="reports-schedule-report-modal__custom-day-input"
                  value={customDay}
                  error={customDayInvalid}
                  helperText={
                    customDayInvalid ? 'Enter a day from 1 to 31.' : undefined
                  }
                  onChange={(event) =>
                    setCustomDay(parseInt(event.target.value, 10) || 0)
                  }
                  clearable={false}
                />
                <span className="reports-schedule-report-modal__custom-day-suffix">
                  {Number.isFinite(customDay) &&
                  customDay >= 1 &&
                  customDay <= 31
                    ? ordinalSuffix(customDay)
                    : 'th'}{' '}
                  day
                </span>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </Modal>
  )
}

export default ScheduleReportModal
